/* The month-spined date strip, shared by the client booking flow and the admin
   composer (RULED 2026-08-19 — reuse, never re-paste).

   GEOMETRY is the device-proven client strip, copied byte-for-byte from
   `SessionDetailPage.module.css` `.dateStrip` … `.dateChip.disabled .dnum` as
   it shipped. Do not "improve" it here; it has survived device smoke.

   THEME is deliberately absent. This file names ONLY its own `--strip-*`
   custom properties and no token from either world — the client's per-tenant
   injected tokens and the admin's `--color-*` globals must never meet. Each
   host maps its own palette onto these at the usage site:

     --strip-spine-bg, --strip-accent, --strip-accent-rgb, --strip-spine-font,
     --strip-chip-bg, --strip-chip-ring, --strip-dow-color, --strip-dnum-color,
     --strip-selected-dow-color

   They carry no fallbacks on purpose: an unmapped host should look obviously
   broken rather than quietly borrow the other world's palette. */

.strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  /* No LEFT padding: the sticky month spine pins flush to the strip's left edge
     (the spine itself is the left gutter). Right padding keeps the last day off
     the edge. (CWR 2.4.1-B) */
  padding: 2px 20px 2px 0;
}
.strip::-webkit-scrollbar {
  display: none;
}

/* ── Sticky month spine (B): each month's days scroll BEHIND its opaque label,
   which pins at left:0 until the next month's spine shoves it out. ── */
.month {
  display: flex;
  gap: 8px;
  /* NON-SHRINKING. A flex item shrinks by default, and a compressed group spills
     its non-shrinkable chips under the NEXT month's sticky spine (the admin copy
     hit exactly this). The group must size to its content and let the STRIP
     scroll. Layout-neutral for the client, whose `min-width: auto` was already
     holding the group open — this states the rule instead of relying on it. */
  flex: 0 0 auto;
}
.spine {
  position: sticky;
  left: 0;
  z-index: 3;
  flex: 0 0 auto;
  align-self: stretch;
  display: flex;
  align-items: center;
  background: var(--strip-spine-bg);
  /* accent hairline on the spine's RIGHT edge — between the month name and the days */
  border-right: 1px solid rgba(var(--strip-accent-rgb), 0.4);
}
.spineLabel {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-family: var(--strip-spine-font);
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.03em;
  color: var(--strip-accent);
  padding: 9px 5px;
}

.chip {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  flex: 0 0 auto;
  min-width: 52px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 9px 10px;
  border-radius: 14px;
  background: var(--strip-chip-bg);
  box-shadow: inset 0 0 0 1px var(--strip-chip-ring);
  cursor: pointer;
}
.chip .dow {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--strip-dow-color);
}
.chip .dnum {
  font-size: 17px;
  font-weight: 600;
  color: var(--strip-dnum-color);
  letter-spacing: -0.02em;
}
.chip .dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--strip-accent);
  margin-top: 1px;
}
.chip.selected {
  background: rgba(var(--strip-accent-rgb), 0.18);
  box-shadow: inset 0 0 0 1px rgba(var(--strip-accent-rgb), 0.6), 0 0 18px rgba(var(--strip-accent-rgb), 0.18);
}
.chip.selected .dow {
  color: var(--strip-selected-dow-color);
}
.chip.disabled {
  opacity: 0.34;
  cursor: default;
}
/* The client strikes an unavailable day; the admin's disabled means "the package
   is full", where a strike would read as cancelled. Hosts opt in. */
.chip.disabled .dnum {
  text-decoration: var(--strip-disabled-num-decoration, none);
}
